import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clock, Building2, User, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { salesApi } from '@/services/api';
import type { Transaction } from '@/types';

interface RecentSalesProps {
  limit?: number;
}

export function RecentSales({ limit = 10 }: RecentSalesProps) {
  const { t, i18n } = useTranslation();

  const { data, isLoading } = useQuery<{ transactions: Transaction[] }>({
    queryKey: ['recent-sales', limit],
    queryFn: () => salesApi.getRecent(limit),
    refetchInterval: 30000,
  });

  const transactions = data?.transactions || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatTime = (date: string | Date) => {
    const d = new Date(date);
    return d.toLocaleTimeString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    return d.toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Transactions are already sorted and limited by the API
  const recentTransactions = transactions;

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4 animate-pulse"></div>
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-5 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('recentSales.title')}</h2>

      {recentTransactions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('recentSales.noSales')}</div>
      ) : (
        <div className="space-y-4">
          {recentTransactions.map((tx) => (
            <div
              key={tx._id}
              className={cn(
                'flex items-center gap-4 p-3 rounded-lg transition-colors',
                tx.isPending ? 'bg-orange-50' : 'hover:bg-gray-50'
              )}
            >
              {/* Icon */}
              <div
                className={cn(
                  'p-2 rounded-lg',
                  tx.isPending ? 'bg-orange-100' : 'bg-blue-100'
                )}
              >
                <Building2
                  className={cn(
                    'w-5 h-5',
                    tx.isPending ? 'text-orange-600' : 'text-blue-600'
                  )}
                />
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{tx.companyName}</p>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {tx.salesAgentName}
                  </span>
                  {tx.paymentMethod && (
                    <span className="flex items-center gap-1">
                      <CreditCard className="w-3 h-3" />
                      {tx.paymentMethod}
                    </span>
                  )}
                  {tx.paymentDate && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(tx.paymentDate)} {formatTime(tx.paymentDate)}
                    </span>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="text-end">
                <p
                  className={cn(
                    'font-bold',
                    tx.isPending ? 'text-orange-600' : 'text-green-600'
                  )}
                >
                  {formatCurrency(tx.amount)}
                </p>
                {tx.isPending && (
                  <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                    {t('recentSales.pending')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
